/**
 * زمینه‌ی `meta` و نشست.
 *
 * `meta` نقطه‌ی بوت است: حالت اجرا، پروفایل و همه‌ی برچسب‌های فارسی از آن
 * می‌آید. تا وقتی نرسیده، هیچ صفحه‌ای رندر نمی‌شود — رندر نیمه‌بارگذاری باعث
 * می‌شد برچسب‌ها با تأخیر بیایند و متن جابه‌جا بپرد.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

import { clearToken, getToken, setToken } from './api/client'
import { fetchMeta } from './api/endpoints'
import { queryKeys } from './queries'

import type { Meta } from './types'

interface MetaContextValue {
  meta: Meta
  /** آیا کاربر اجازه‌ی دیدن مسیرهای مشاوره را دارد؟ */
  isAuthenticated: boolean
  signIn: (token: string) => void
  signOut: () => void
}

const MetaContext = createContext<MetaContextValue | null>(null)

interface ProviderProps {
  meta: Meta
  children: ReactNode
}

export const MetaProvider = ({ meta, children }: ProviderProps) => {
  // حالت اولیه از توکن ذخیره‌شده می‌آید تا بارگذاری دوباره‌ی صفحه، کاربر را
  // بیرون نیندازد. در حالت کارگاه احراز هویتی نیست، پس همیشه مجاز است.
  const [token, setTokenState] = useState(() => getToken())

  const value = useMemo<MetaContextValue>(
    () => ({
      meta,
      isAuthenticated: !meta.access_required || token !== '',
      signIn: (next: string) => {
        setToken(next)
        setTokenState(next)
      },
      signOut: () => {
        clearToken()
        setTokenState('')
      },
    }),
    [meta, token],
  )

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>
}

/**
 * دسترسی به `meta`.
 *
 * اگر بیرون از provider صدا زده شود خطا می‌دهد، نه مقدار پیش‌فرض. مقدار
 * پیش‌فرض یعنی صفحه با پروفایل اشتباه رندر شود و کسی نفهمد.
 */
export const useMeta = (): MetaContextValue => {
  const value = useContext(MetaContext)
  if (value === null) {
    throw new Error('useMeta باید داخل MetaProvider استفاده شود.')
  }
  return value
}

/**
 * query بوت. جدا نگه داشته شده تا `App` بتواند حالت بارگذاری را رندر کند.
 *
 * مسیر عمومی با هدر هر درخواست می‌رود (interceptor در `client.ts`) و سرور با آن
 * می‌گوید این نشانی به کدام دستیار منتشرشده وصل است. مخاطب (پنل یا مشتری) را هم
 * **سرور** از میزبان درخواست تعیین می‌کند، نه این کلاینت.
 */
export const useMetaQuery = (): UseQueryResult<Meta, Error> =>
  useQuery({
    queryKey: queryKeys.meta,
    queryFn: fetchMeta,
    // برچسب‌ها و مخاطب در طول یک نشست عوض نمی‌شوند.
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  })
